import {
  logEvent,
  setUserId,
  setUserProperties,
} from 'firebase/analytics';
import { ALL_ROLES, type UserRole } from '../config/accessControl';
import { analyticsReady } from '../firebase';

export const ANALYTICS_EVENTS = {
  login: 'login',
  signUp: 'sign_up',
  logout: 'logout',
  generateLead: 'generate_lead',
  ctaClick: 'cta_click',
  residentCreated: 'resident_created',
  taskCreated: 'task_created',
  taskCompleted: 'task_completed',
  medicationAdministered: 'medication_administered',
  vitalRecorded: 'vital_recorded',
  reportDownloaded: 'report_downloaded',
} as const;

export type AnalyticsEventName =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

const ALLOWED_EVENTS = new Set<string>(Object.values(ANALYTICS_EVENTS));

const ALLOWED_PARAM_KEYS = new Set([
  'method',
  'form',
  'location',
  'destination',
  'report_type',
  'page_path',
  'page_title',
  'page_location',
]);

const ALLOWED_PARAM_VALUES: Record<string, ReadonlySet<string>> = {
  method: new Set(['email']),
  form: new Set(['request_demo', 'contact']),
  location: new Set(['header', 'mobile_menu', 'home_hero', 'cta_section']),
  destination: new Set([
    'request-demo',
    'login',
    'register',
    'features',
    'pricing',
    'contact',
  ]),
  report_type: new Set(['behavior']),
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const OBJECT_ID_RE = /^[0-9a-f]{24}$/i;

const isAllowedRole = (role: string): role is UserRole =>
  (ALL_ROLES as readonly string[]).includes(role);

const isIdSegment = (segment: string): boolean =>
  UUID_RE.test(segment) || OBJECT_ID_RE.test(segment);

/** Replace dynamic ids with route templates and drop query/hash. */
export const toTemplatedPath = (pathname: string): string => {
  const path = (pathname || '/').split('?')[0].split('#')[0] || '/';
  if (/^\/residents\/[^/]+$/.test(path)) {
    return '/residents/:id';
  }
  if (/^\/residents\/[^/]+\//.test(path)) {
    return path.replace(/^\/residents\/[^/]+/, '/residents/:id');
  }
  return path
    .split('/')
    .map((segment) => {
      if (!segment) return segment;
      if (isIdSegment(segment)) return ':id';
      return segment;
    })
    .join('/');
};

export const destinationFromPath = (path: string): string | undefined => {
  const slug = path.replace(/^\//, '').replace(/\/$/, '');
  if (ALLOWED_PARAM_VALUES.destination.has(slug)) return slug;
  return undefined;
};

export const sanitizeEventParams = (
  params: Record<string, string | undefined> | undefined
): Record<string, string> => {
  if (!params) return {};
  const sanitized: Record<string, string> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (!ALLOWED_PARAM_KEYS.has(key) || value == null || value === '') return;
    const allowedValues = ALLOWED_PARAM_VALUES[key];
    if (allowedValues && !allowedValues.has(value)) return;
    sanitized[key] = value;
  });
  return sanitized;
};

const runWhenReady = (fn: (instance: NonNullable<Awaited<typeof analyticsReady>>) => void): void => {
  void analyticsReady.then((instance) => {
    if (!instance) return;
    fn(instance);
  });
};

/** Log a templated page_view. Drops search strings and raw record ids. */
export const logPageView = (pathname: string): void => {
  const pagePath = toTemplatedPath(pathname);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const title = typeof document !== 'undefined' ? document.title : '';
  runWhenReady((instance) => {
    logEvent(instance, 'page_view', {
      page_path: pagePath,
      page_title: title,
      page_location: `${origin}${pagePath}`,
    });
  });
};

export const trackEvent = (
  name: AnalyticsEventName,
  params?: Record<string, string | undefined>
): void => {
  if (!ALLOWED_EVENTS.has(name)) return;
  const sanitized = sanitizeEventParams(params);
  runWhenReady((instance) => {
    logEvent(instance, name as never, sanitized);
  });
};

export const trackCtaClick = (location: string, destination: string): void => {
  trackEvent(ANALYTICS_EVENTS.ctaClick, { location, destination });
};

export const identifyAnalyticsUser = (user: {
  id: string;
  role: string;
  facilityId?: string;
}): void => {
  if (!user.id) return;
  const role = isAllowedRole(user.role) ? user.role : 'unknown';
  const hasFacility = user.facilityId ? 'true' : 'false';
  runWhenReady((instance) => {
    setUserId(instance, user.id);
    setUserProperties(instance, {
      role,
      has_facility: hasFacility,
    });
  });
};

export const clearAnalyticsUser = (): void => {
  runWhenReady((instance) => {
    setUserId(instance, null);
    setUserProperties(instance, {
      role: null,
      has_facility: null,
    });
  });
};
